import { Injectable } from '@angular/core';
import { ThemeService } from './theme.service';

@Injectable({
  providedIn: 'root'
})
export class BackwardCompatibilityTestService {

  constructor(private themeService: ThemeService) {}

  /**
   * Test backward compatibility with existing theming system
   */
  async testBackwardCompatibility(): Promise<{
    legacy: boolean;
    partner: boolean;
    fallback: boolean;
    switching: boolean;
    overall: boolean;
  }> {
    console.log('🔄 Running backward compatibility tests...');

    const results = {
      legacy: await this.testLegacyThemeLoading(),
      partner: await this.testPartnerThemeSystem(),
      fallback: await this.testFallbackBehavior(),
      switching: await this.testThemeSwitching(),
      overall: false
    };

    results.overall = Object.values(results).slice(0, -1).every(r => r);

    console.log('✅ Backward compatibility test results:', results);
    return results;
  }

  /**
   * Test legacy theme loading (switchTheme method)
   */
  private async testLegacyThemeLoading(): Promise<boolean> {
    try {
      console.log('  Testing legacy theme loading...');

      // Test switching to a legacy theme
      await this.themeService.switchTheme('sparkasse');

      // Wait for theme to load
      await new Promise(resolve => setTimeout(resolve, 500));

      const themeState = this.themeService.themeState$.getValue();

      // Check that legacy theme loaded correctly
      const legacyWorking = themeState.currentTheme === 'sparkasse' &&
                           !themeState.partnerTheme &&
                           !themeState.isLoading &&
                           !themeState.error;

      // Test clearing theme
      await this.themeService.switchTheme(undefined);
      await new Promise(resolve => setTimeout(resolve, 200));

      const clearedState = this.themeService.themeState$.getValue();
      const clearingWorking = !clearedState.currentTheme;

      console.log(`    Legacy theme loading: ${legacyWorking ? 'PASS' : 'FAIL'}`);
      console.log(`    Theme clearing: ${clearingWorking ? 'PASS' : 'FAIL'}`);

      return legacyWorking && clearingWorking;
    } catch (error) {
      console.error('    Legacy theme test failed:', error);
      return false;
    }
  }

  /**
   * Test partner theme system doesn't interfere with legacy
   */
  private async testPartnerThemeSystem(): Promise<boolean> {
    try {
      console.log('  Testing partner theme system isolation...');

      // Test that partner theme methods exist and are callable
      const partnerMethodsExist = !!(
        typeof this.themeService.getCurrentPartner === 'function' &&
        typeof this.themeService.getCurrentPartnerSubfolder === 'function' &&
        typeof this.themeService.loadPartnerTheme === 'function' &&
        typeof this.themeService.getCustomProperties === 'function'
      );

      // Test that calling partner methods doesn't break legacy functionality
      const customProps = this.themeService.getCustomProperties();
      const currentPartner = this.themeService.getCurrentPartner();

      console.log(`    Partner methods exist: ${partnerMethodsExist ? 'PASS' : 'FAIL'}`);
      return partnerMethodsExist;
    } catch (error) {
      console.error('    Partner theme system test failed:', error);
      return false;
    }
  }

  /**
   * Test fallback behavior maintains legacy support
   */
  private async testFallbackBehavior(): Promise<boolean> {
    try {
      console.log('  Testing fallback behavior...');

      // Create a mock partner data with fallback enabled but non-existent theme
      const mockPartnerData = {
        partnerId: 'test',
        name: 'Test Partner',
        themeTemplateToken: 'sparkasse', // Legacy fallback
        partnerLogoUrl: '',
        theme: {
          cssFileName: 'non-existent-theme.css',
          fallbackEnabled: true,
          version: '1.0.0'
        },
        products: []
      };

      // This should fall back to the legacy theme
      await this.themeService.loadPartnerTheme(mockPartnerData);
      await new Promise(resolve => setTimeout(resolve, 1000));

      const themeState = this.themeService.themeState$.getValue();

      // Should have fallen back to legacy theme (sparkasse)
      const fallbackWorking = themeState.fallbackActive &&
                              themeState.currentTheme === 'sparkasse';

      console.log(`    Fallback to legacy: ${fallbackWorking ? 'PASS' : 'FAIL'}`);
      return fallbackWorking;
    } catch (error) {
      console.error('    Fallback behavior test failed:', error);
      return false;
    }
  }

  /**
   * Test theme switching between legacy and partner themes
   */
  private async testThemeSwitching(): Promise<boolean> {
    try {
      console.log('  Testing theme switching...');

      // Switch to legacy theme
      await this.themeService.switchTheme('sparkasse');
      await new Promise(resolve => setTimeout(resolve, 300));

      const legacyState = this.themeService.themeState$.getValue();
      const legacySwitched = legacyState.currentTheme === 'sparkasse' && !legacyState.partnerTheme;

      // Switch to another legacy theme
      await this.themeService.switchTheme('europace');
      await new Promise(resolve => setTimeout(resolve, 300));

      const anotherLegacyState = this.themeService.themeState$.getValue();
      const anotherLegacySwitched = anotherLegacyState.currentTheme === 'europace';

      // Clear theme
      await this.themeService.switchTheme(undefined);
      await new Promise(resolve => setTimeout(resolve, 200));

      const clearedState = this.themeService.themeState$.getValue();
      const themeCleared = !clearedState.currentTheme;

      const allSwitchingWorking = legacySwitched && anotherLegacySwitched && themeCleared;

      console.log(`    Legacy theme switching: ${allSwitchingWorking ? 'PASS' : 'FAIL'}`);
      return allSwitchingWorking;
    } catch (error) {
      console.error('    Theme switching test failed:', error);
      return false;
    }
  }

  /**
   * Test specific legacy theme paths
   */
  async testLegacyThemePaths(): Promise<{ [themeName: string]: boolean }> {
    const legacyThemes = ['sparkasse', 'europace'];
    const results: { [themeName: string]: boolean } = {};

    for (const themeName of legacyThemes) {
      try {
        await this.themeService.switchTheme(themeName);
        await new Promise(resolve => setTimeout(resolve, 300));

        const themeState = this.themeService.themeState$.getValue();
        results[themeName] = themeState.currentTheme === themeName && !themeState.error;

        console.log(`    Legacy theme '${themeName}': ${results[themeName] ? 'PASS' : 'FAIL'}`);
      } catch (error) {
        console.error(`    Legacy theme '${themeName}' failed:`, error);
        results[themeName] = false;
      }
    }

    return results;
  }

  /**
   * Generate backward compatibility report
   */
  async generateBackwardCompatibilityReport(): Promise<string> {
    const testResults = await this.testBackwardCompatibility();
    const legacyThemeResults = await this.testLegacyThemePaths();

    return `
# Backward Compatibility Test Report

## Overall Status: ${testResults.overall ? 'COMPATIBLE ✅' : 'ISSUES DETECTED ❌'}

### Core Compatibility Tests
- Legacy Theme Loading: ${testResults.legacy ? 'PASS ✅' : 'FAIL ❌'}
- Partner Theme System: ${testResults.partner ? 'PASS ✅' : 'FAIL ❌'}
- Fallback Behavior: ${testResults.fallback ? 'PASS ✅' : 'FAIL ❌'}
- Theme Switching: ${testResults.switching ? 'PASS ✅' : 'FAIL ❌'}

### Legacy Theme Path Tests
${Object.entries(legacyThemeResults)
  .map(([theme, result]) => `- ${theme}: ${result ? 'PASS ✅' : 'FAIL ❌'}`)
  .join('\n')}

### Compatibility Summary
${testResults.overall
  ? '✅ The new partner theme system maintains full backward compatibility with existing theme functionality.'
  : '❌ Compatibility issues detected. Legacy theme functionality may be impacted.'
}

### Notes
- Legacy theme switching via switchTheme() method works as before
- Partner theme system runs independently and doesn't interfere with legacy themes
- Fallback mechanism properly reverts to legacy themes when partner themes fail
- All existing theme template tokens (sparkasse, europace) continue to work

Generated: ${new Date().toISOString()}
    `.trim();
  }
}
